<template>
  <slot v-if="isLoading" name="loading">
    <div class="spinner"></div>
  </slot>
  <slot v-else-if="errors" name="errors" :errors="errors">
    <Alert variant="destructive" class="flex gap-2 items-center justify-between">
      <div class="flex gap-2 items-center">
        <vue-feather type="alert-triangle" class="h-4 w-4" />
        <AlertDescription>{{ errors.join(', ') }}</AlertDescription>
      </div>
      <Button v-if="!hideRetry" variant="outline" size="sm" class="gap-1.5" :disabled="isCoolingDown" @click="retry">
        <vue-feather type="refresh-cw" class="h-3 w-3" />
        Retry
      </Button>
    </Alert>
  </slot>
  <slot v-else :data="data as T" />
</template>

<script setup lang="ts" generic="T">
import type { useHFetchQuery } from './useHFetchQuery';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { onUnmounted, ref, watch } from 'vue';
import { toast } from 'vue-sonner';

const RETRY_COOLDOWN_MS = 3000;

const { query, hideRetry = false } = defineProps<{
  query: ReturnType<typeof useHFetchQuery<T>>;
  hideRetry?: boolean;
}>();

defineSlots<{
  loading?: () => unknown;
  errors?: (props: { errors: string[] }) => unknown;
  default: (props: { data: T }) => unknown;
}>();

const { data, errors, isLoading, reload } = query;

watch(errors, newErrors => {
  if (newErrors?.length) {
    toast.error('Failed to load data', {
      description: 'If this error persists please contact support@humaniz.io',
    });
  }
});

const isCoolingDown = ref(false);
let cooldownTimer: ReturnType<typeof setTimeout> | undefined;

const retry = () => {
  if (isCoolingDown.value) return;
  isCoolingDown.value = true;
  reload();
  cooldownTimer = setTimeout(() => {
    isCoolingDown.value = false;
  }, RETRY_COOLDOWN_MS);
};

onUnmounted(() => clearTimeout(cooldownTimer));
</script>
